import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart, Star } from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [referralBanner, setReferralBanner] = useState(null);
  const { addToCart } = useCart();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setWishlist(JSON.parse(localStorage.getItem('ppc_wishlist') || '[]'));
    fetchProducts();

    // Handle referral link
    const ref = searchParams.get('ref');
    if (ref) {
      handleReferral(ref);
    }
  }, []);

  const handleReferral = async (refCode) => {
    try {
      const res = await axios.get(`${API}/referral/${refCode}`);
      if (res.data.valid) {
        // Generate a friend discount code
        const codeRes = await axios.post(`${API}/referral/generate-friend-code/${refCode}`);
        setReferralBanner({
          referrerName: res.data.referrer_name,
          discountCode: codeRes.data.discount_code,
          amount: codeRes.data.amount,
        });
        // Store in sessionStorage so it persists to checkout
        sessionStorage.setItem('referral_discount_code', codeRes.data.discount_code);
        toast.success(`You've got R${codeRes.data.amount} off your combo purchase!`);
      }
    } catch {
      // Invalid referral code - ignore silently
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      // Fetch ratings for all products
      const ratingPromises = response.data.map(p =>
        axios.get(`${API}/reviews/${p.id}`).then(r => ({ id: p.id, ...r.data })).catch(() => ({ id: p.id, average_rating: 0, total_reviews: 0 }))
      );
      const allRatings = await Promise.all(ratingPromises);
      const ratingMap = {};
      allRatings.forEach(r => { ratingMap[r.id] = { avg: r.average_rating, count: r.total_reviews }; });
      setRatings(ratingMap);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const toggleWishlist = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const current = JSON.parse(localStorage.getItem('ppc_wishlist') || '[]');
    const exists = current.some(p => p.id === product.id);
    let updated;
    if (exists) {
      updated = current.filter(p => p.id !== product.id);
      toast.success('Removed from wishlist');
    } else {
      updated = [...current, { id: product.id, name: product.name, price: product.price, image_url: product.image_url }];
      toast.success('Added to wishlist');
    }
    localStorage.setItem('ppc_wishlist', JSON.stringify(updated));
    setWishlist(updated);
  };

  const isWishlisted = (id) => wishlist.some(p => p.id === id);

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    
    // Get appropriate confirmation message
    let actionText = "added to your routine";
    if (product.is_combo) {
      actionText = "routine started";
    } else if (product.category === "Ritual Reset" || product.category === "Targeted Support") {
      actionText = "care chosen";
    }
    
    toast.success(`${product.name} ${actionText}!`);
  };

  // Get appropriate CTA text for product card
  const getCtaText = (product) => {
    if (product.is_combo) {
      return "Start My Routine";
    } else if (product.category === "Ritual Reset" || product.category === "Targeted Support") {
      return "Choose My Care";
    } else {
      return "Add to My Routine";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-perfect-peach" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Intimate Care Products - Daily & Ritual Wellness | The Perfect Peach Club</title>
        <meta name="description" content="Browse our intentional intimate care collection: daily external care, hydration, and conscious reset support. Gentle, informed wellness for women." />
        <meta name="keywords" content="intimate care products, gentle intimate care, intimate wellness routine, daily intimate care, ritual wellness products" />
      </Helmet>
      
      <Navigation />
      <ChatWidget />

      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-8 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2420] mb-3 md:mb-4">
            Our Products
          </h1>
          <p className="text-african-earth text-base md:text-lg max-w-2xl mx-auto px-2">
            Discover our complete collection of natural feminine wellness products
          </p>
        </div>

        {/* Referral discount banner */}
        {referralBanner && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-8 text-center" data-testid="referral-discount-banner">
            <p className="text-green-800 font-medium">
              {referralBanner.referrerName} sent you <strong>R{referralBanner.amount} off</strong> your combo purchase!
            </p>
            <p className="text-green-700 text-sm mt-1">
              Code <strong className="font-mono">{referralBanner.discountCode}</strong> will be auto-applied at checkout
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {products.map((product) => {
            const isOutOfStock = product.stock === 0;
            const isLowStock = product.stock > 0 && product.stock <= 5;
            
            return (
            <div
              key={product.id}
              className={`bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-floating transition-all duration-500 group border border-[#F0EAE6] ${isOutOfStock ? 'opacity-75' : ''}`}
              data-testid={`product-card-${product.id}`}
            >
              <Link to={`/product/${product.id}`}>
                <div className="aspect-square overflow-hidden relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${isOutOfStock ? 'grayscale' : ''}`}
                  />
                  {/* Wishlist heart */}
                  <button
                    onClick={(e) => toggleWishlist(e, product)}
                    className="absolute top-3 left-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:scale-110 transition-transform z-10 shadow-sm"
                    data-testid={`wishlist-toggle-${product.id}`}
                  >
                    <Heart className={`w-4 h-4 ${isWishlisted(product.id) ? 'text-red-500 fill-red-500' : 'text-african-earth/60'}`} />
                  </button>
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="bg-white/90 text-[#2D2420] px-4 py-2 rounded-full text-sm font-medium">
                        Out of Stock
                      </span>
                    </div>
                  )}
                  {isLowStock && !isOutOfStock && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-[#FEF3E7] text-[#B8860B] px-3 py-1 rounded-full text-xs font-medium">
                        Low Stock
                      </span>
                    </div>
                  )}
                </div>
              </Link>
              <div className="p-6">
                {product.is_combo && (
                  <span className="inline-block bg-royal-gold text-white px-3 py-1 rounded-full text-xs font-medium mb-3">
                    COMBO DEAL {product.savings ? `• SAVE R${product.savings}` : ''}
                  </span>
                )}
                {product.variant_name && (
                  <span className="inline-block bg-perfect-peach/10 text-perfect-peach px-3 py-1 rounded-full text-xs font-medium mb-2">
                    {product.variant_name}
                  </span>
                )}
                {product.bundle_includes && (
                  <p className="text-xs text-african-earth/60 mb-2">
                    Includes: {product.bundle_includes.join(', ')}
                  </p>
                )}
                <Link to={`/product/${product.id}`}>
                  <h3 className="font-playfair text-xl font-semibold text-[#2D2420] mb-1 group-hover:text-perfect-peach transition-colors duration-300">
                    {product.name}
                  </h3>
                </Link>
                {/* Star rating */}
                {ratings[product.id]?.count > 0 && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(ratings[product.id]?.avg) ? 'text-royal-gold fill-royal-gold' : 'text-[#E0D5D0]'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-african-earth/60">({ratings[product.id]?.count})</span>
                  </div>
                )}
                <p className="text-african-earth text-sm mb-4 line-clamp-3">
                  {product.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    {product.size && (
                      <span className="text-xs text-african-earth/60 mb-0.5">{product.size}</span>
                    )}
                    <span className="text-2xl font-bold text-perfect-peach">
                      R{product.price.toFixed(2)}
                    </span>
                    {product.original_price && (
                      <span className="text-sm text-african-earth/60 line-through">
                        R{product.original_price.toFixed(2)}
                      </span>
                    )}
                  </div>
                  {isOutOfStock ? (
                    <span className="text-african-earth/50 text-sm font-medium px-6 py-2">
                      Sold Out
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="bg-perfect-peach text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-peach-dark transition-all duration-300 hover:-translate-y-1"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {getCtaText(product)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;